// The execution table's columns, as data.
//
// Each column names itself, says how it sorts, says how it draws, and says how it exports --
// so a *view* is a choice of columns rather than a second table. That shape was written for
// GRO-58 and this is it being used: the twelve columns the single hybrid table carried are now
// eight, and the six per-channel counts that made it a scoreboard live in one cell. The CSV
// on `/execution` walks this same array.
//
// What each column is *for* is the whole argument. A worklist column has to change what
// somebody does in the next ten minutes. "Email replies: 2" does not; "nobody here is
// reachable" does. So the counts collapsed and four questions that were previously answered
// only by opening the panel -- or not at all -- became columns:
//
//   Why now      what makes this account worth a touch today          (book/derived.ts)
//   Staff        how big they are, at a glance                        (already in the payload)
//   Reachable    whether this row can be acted on at all              (derived from contacts)
//   Last out     who we last wrote to, and which of our reps sent it  (derived from timeline)
//
// `sortValue` returning null is meaningful and is not the same as returning 0: sort.ts pushes
// nulls last in both directions, because an account with no playground org has no credit
// figure at all and must not read as a real zero.

import { WorkReasonView } from './WorkReasonView';
import type { ReactNode } from 'react';
import type { BookAccount, Viewer } from '../api/types';
import { SharedSlackFlag } from './AccountFlagChips';
import { hasSharedSlack, pastOpportunityChip, pastOpportunityDetail, pastOpportunityOf } from './accountFlags';
import { autoLaneTitle, meetingBooked } from './boardPref';
import { ACCOUNT_FLOOR_PX } from './columnFit';
import {
  ACCOUNT_CSV, AE_CSV, CONTACTS_CSV, CREDITS_CSV, EMPLOYEES_CSV, FUNDING_CSV, LAST_OUT_CSV,
  PLAN_CSV, RANK_CSV, RELATIONSHIP_CSV, SIGNAL_CSV, WHY_NOW_CSV,
} from './csvColumns';
import {
  VISIBLE_TIES, fundShortName, fundingSortValue, readFunding, readRelationship,
  relationshipSortValue, roundIsRecent, tieTooltip,
} from './funding';
import type { FundTie } from '../api/types';
import {
  WHY_NOW_RANK, lastOutbound, reachableContacts, recentLostDeal, whyNow,
} from './derived';
import type { RankedAccount } from './derived';
import { companyUrl, daysSince, formatCount, formatCredits, titleCase } from '../lib/format';

export interface CellContext {
  today: Date;
  staleDays: number;
  // The book's `fit_score_ceiling`, for the v2 design loop's Fit bar. Unused by these columns.
  fitCeiling?: number;
  // Dev only (design loop): "<account id>:<column key>" pins that cell's hover card open.
  pinnedHover?: string;
  // The High priority flag's toggle (`PriorityFlagButton.tsx`). Absent: the column shows the flag read-only.
  priority?: PriorityCellContext;
}

// What the High priority column needs to draw an enabled or locked toggle and to send a click.
export interface PriorityCellContext {
  viewer: Viewer | null | undefined;
  pendingId: string | null;
  onToggle: (row: BookAccount) => void;
}

export interface BookColumn {
  key: string;
  label: string;
  numeric?: boolean;
  // Rendered as a `<col>`, so the table sizes from this array rather than from whatever the
  // widest cell in each column happens to hold. Without it the numeric columns took whatever
  // was left once Account and Signal had grown to fit their longest row, and the layout
  // shifted every time a filter changed which rows were on screen.
  //
  // Account is deliberately the one column with no width: it absorbs the slack, because it is
  // the one whose content is worth more room when there is room. `minWidth` is the deficit
  // case that comment missed — below it, columnFit.ts drops other columns rather than
  // squeezing the name toward zero.
  width?: string;
  minWidth?: number;
  // Shares the table's spare width (its `width` is then a floor, drawn with no `<col>` width).
  // Unset: only Account does, as on the board. The AE table also lets Next step grow.
  flex?: boolean;
  // Shown in the header's tooltip. Several of these columns are derived readings rather than
  // stored fields, and a reader should be able to find out what one means without opening the
  // source -- "Reachable" in particular looks like a count of contacts and is not.
  hint?: string;
  sortValue: (row: RankedAccount) => string | number | null;
  // Plain text for the CSV export. The cell renderer is JSX and cannot be written to a
  // spreadsheet; this is what a new column has to supply or the download is missing a field.
  accessor: (row: RankedAccount) => string;
  cell: (row: RankedAccount, context: CellContext) => ReactNode;
}

// Plan cannot sort on its label. Alphabetically 'free' beats 'enterprise', which is the same
// trap the SQL rollup avoids by ranking numerically.
const TIER_RANK: Record<string, number> = {
  enterprise: 5, scale: 4, startup: 3, pro: 2, free: 1,
};

// Percentage change in credit burn. Rendered next to the number rather than instead of it,
// because a big percentage on a tiny base is noise.
function TrendChip({ percent }: { percent: number | null }) {
  if (percent == null) return null;
  return (
    <span className={'trend ' + (percent > 0 ? 'up' : 'down')}>
      {percent > 0 ? '+' : ''}{Math.round(percent)}%
    </span>
  );
}

// The account name goes to the company, not to Salesforce. `domain` is populated on 313 of the
// 314 accounts and `website` on 261; the one account with neither renders as plain text rather
// than a dead link, and Salesforce is still one click away in the panel.
// The name on one line and its flags on the next, rather than flags trailing the name inline.
//
// Inline, a long name pushed its own flags onto a third line while a short one kept them on the
// first, so no two rows were the same height and the flags never lined up down the column.
// Stacked, the flags form their own column inside the cell: same place on every row, same width
// as each other, scannable without reading the names above them.
//
// The flags say the same kind of thing -- this is not a cold account. Slack and a
// recent loss were the first two; a booked sitting is the third, so the amber row
// does not rely on colour alone. "Booked" rather than "Meeting booked" because
// every flag is a fixed 74px and has to line up down the column.
function AccountCell({ row, context }: { row: BookAccount; context: CellContext }) {
  const href = companyUrl(row.domain, row.website);
  const lost = recentLostDeal(row, context.today);
  const booked = meetingBooked(row);
  const sharedSlack = hasSharedSlack(row);
  const past = pastOpportunityOf(row);
  return (
    <>
      <div className="acctline" title={row.account}>
        {href ? (
          <a href={href} target="_blank" rel="noopener">
            {row.account}
          </a>
        ) : (
          row.account
        )}
      </div>
      {sharedSlack || past || lost || booked || row.auto_lane ? (
        <div className="acctflags">
          {sharedSlack ? (
            <SharedSlackFlag url={row.pylon_slack_url} />
          ) : null}
          {past ? (
            <span className="flag past" title={'Past opportunity: ' + pastOpportunityDetail(past)}>
              {pastOpportunityChip(past)}
            </span>
          ) : null}
          {lost ? (
            <span
              className="flag lost"
              title={`A deal with this company was closed lost in ${lost.when}. They have evaluated us — read the history before opening cold.`}
            >
              Lost {lost.when}
            </span>
          ) : null}
          {booked ? (
            <span
              className="flag booked"
              title="A meeting has been booked with this company — it is no longer outbound work"
            >
              Booked
            </span>
          ) : null}
          {row.auto_lane ? (
            <span className="flag auto" title={autoLaneTitle(row) ?? undefined}>
              Auto
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

// The strongest reason to act on this row today. See `whyNow` for the order and why a shared
// Slack channel is deliberately not one of the reasons.
function WhyNowCell({ row }: { row: RankedAccount }) {
  const reason = whyNow(row, row.book_today);
  if (!reason) return <span className="dash">—</span>;
  return (
    <>
      <span className={'whychip ' + reason.tone}>{reason.label}</span>
      {/* Clamped to one line, with the whole thing in the tooltip. The engine's `tier_reason`
          runs to a full diagnostic sentence on some accounts -- "weak | evidence=service_offering
          | ships=no | base 2 -> Tier 2 …" -- which took the row three lines tall and buried
          every other cell on it. */}
      {reason.detail ? (
        <div className="sm whydetail" title={reason.detail}>{reason.detail}</div>
      ) : null}
    </>
  );
}

// Who we last wrote to here, and which of our reps sent it -- the two questions the old "Last
// touch" and "Also worked by" columns answered separately and incompletely.
//
// The rep's name renders as a warning chip when it is not the account's owner. That is the
// collision flag, kept alive rather than dropped with the column it used to live in: the thing
// it prevents is two reps emailing into the same live conversation, and it has to be visible
// before the panel is opened.
// Two lines, and never three. Date and staleness on the first, who and which rep on the
// second, both clipped rather than wrapped -- a cell that grows to three lines on the one
// account with a long contact name drags the whole row with it.
//
// The rep is named rather than called "you": three of the four roles reading this board own
// none of it, and a strategist can be looking at a colleague's, so "you" is wrong more often
// than it is right. The warning colour carries the collision instead.
function LastOutCell({ row, context }: { row: BookAccount; context: CellContext }) {
  const last = lastOutbound(row);
  if (!last) return <span className="zero">never</span>;

  const staleFor = daysSince(last.day, context.today);
  const isStale = staleFor >= context.staleDays;
  // Whichever of the two we have. Both is the common case; a touch logged against the company
  // with nobody attached has only the rep, and neither leaves the line empty rather than
  // printing an apology for a fact the mirror does not hold.
  const second = [last.who, last.author].filter(Boolean).join(' · ');

  return (
    <>
      <div className="lastday">
        <span className="mono">{last.day}</span>
        {isStale ? <span className="lastage"> · {staleFor}d</span> : null}
      </div>
      {second ? (
        <div className={'lastwho' + (last.byOther ? ' other' : '')} title={second}>
          {second}
        </div>
      ) : null}
    </>
  );
}

// Funding: the latest round and when, or Public / Acquired, which change what an intro is worth.
// Every state is the same two plain lines -- what, then how much and when -- so nothing reads as a
// badge. A round in the last 90 days says "new" beside its name, because fresh money is a timing
// cue; the name line is short, so it never clips. "Couldn't find out" is muted and never reads
// as "unfunded".
function FundingCell({ row }: { row: RankedAccount }) {
  const reading = readFunding(row);
  if (reading.kind === 'unknown') {
    return <span className="dash" title={reading.hint}>{reading.label}</span>;
  }
  const recent = reading.kind === 'round' && roundIsRecent(row, row.book_today ?? '');
  return (
    <>
      <div className="fundline" title={reading.hint}>
        {reading.label}
        {recent ? <span className="fundnew" title="A round in the last 90 days"> · new</span> : null}
      </div>
      {/* An acquirer's name can be long, so that line wraps rather than trims. */}
      {reading.detail ? (
        <div className={'sm' + (reading.kind === 'acquired' ? ' fundwrap' : '')} title={reading.detail}>
          {reading.detail}
        </div>
      ) : null}
    </>
  );
}

// VC partner: which of our funds back this company, strongest first, as plain text. A fund in a
// recent round is ink; a small, strategic or older stake is muted, so a strong intro path reads
// before its name does. Two names, then "+N"; every tie is in the tooltips.
function FundName({ tie, last }: { tie: FundTie; last: boolean }) {
  return (
    <>
      <span className={'fundname s' + tie.strength} title={tieTooltip(tie)}>{fundShortName(tie.fund)}</span>
      {last ? null : <span className="fundsep">, </span>}
    </>
  );
}

function RelationshipCell({ row }: { row: RankedAccount }) {
  const reading = readRelationship(row);
  if (reading.kind === 'unknown') {
    return <span className="dash" title={reading.hint}>Couldn’t find out</span>;
  }
  if (reading.kind === 'none') {
    return <span className="zero" title={reading.hint}>None of ours</span>;
  }
  const shown = reading.ties.slice(0, VISIBLE_TIES);
  const rest = reading.ties.slice(VISIBLE_TIES);
  return (
    <div className="fundties">
      {shown.map((tie, index) => (
        <FundName key={tie.fund} tie={tie} last={index === shown.length - 1} />
      ))}
      {rest.length ? (
        <span className="alsomore" title={rest.map(tieTooltip).join('\n')}>+{rest.length}</span>
      ) : null}
    </div>
  );
}

export const EXECUTION_COLUMNS: BookColumn[] = [
  {
    ...ACCOUNT_CSV,
    minWidth: ACCOUNT_FLOOR_PX,
    sortValue: row => row.account,
    cell: (row, context) => <AccountCell row={row} context={context} />,
  },
  {
    ...RANK_CSV,
    width: '56px',
    numeric: true,
    hint: 'Scout’s order for this morning. Empty when this row has no ranking, or the ranking is older than the book.',
    sortValue: row => row.ranking,
    cell: row => (
      row.ranking == null
        ? <span className="dash">—</span>
        : row.ranking
    ),
  },
  {
    ...WHY_NOW_CSV,
    width: '246px',
    hint: 'Scout’s current ranking reason, with a book-snapshot fallback when ranking is unavailable or stale.',
    sortValue: row => row.tag ? WHY_NOW_RANK[row.tag.kind] : null,
    cell: row => <WorkReasonView reason={row.work_reason ?? null} />,
  },
  {
    ...EMPLOYEES_CSV,
    width: '80px',
    numeric: true,
    hint: 'Employees, from account triage, then Salesforce’s own figure, then the OB Engine’s.',
    // The same fallback the panel's identity strip uses. `employee_count` already coalesces
    // triage over Salesforce's `NUMBER_OF_EMPLOYEES` in SQL, so this adds the third source.
    sortValue: row => row.employee_count ?? row.clay?.engine?.employees ?? null,
    cell: row => {
      const count = row.employee_count ?? row.clay?.engine?.employees ?? null;
      return count == null ? <span className="dash">—</span> : formatCount(count);
    },
  },
  {
    ...FUNDING_CSV,
    // The narrowest width that keeps "Couldn't find out" on one line.
    width: '124px',
    hint: 'The latest funding round, from Apollo. Public or Acquired when that applies. '
      + '“new” means a round in the last 90 days. Couldn’t find out is not “unfunded”.',
    sortValue: fundingSortValue,
    cell: row => <FundingCell row={row} />,
  },
  {
    ...RELATIONSHIP_CSV,
    width: '130px',
    hint: 'Which of Cartesia’s ten investors also backs this company, from Apollo’s round data. '
      + 'Dark: a lead-capable fund in a round inside 36 months. Muted: a small, strategic or '
      + 'older stake. Apollo does not say who led or holds a board seat.',
    sortValue: relationshipSortValue,
    cell: row => <RelationshipCell row={row} />,
  },
  {
    ...PLAN_CSV,
    // Plain text, like Funding and VC partner: "Enterprise" as a badge needed 81px and clipped.
    width: '84px',
    sortValue: row => (row.tier ? TIER_RANK[row.tier] ?? 0 : null),
    cell: row => (
      <>
        {row.tier ? (
          <span className={'plantext' + (row.tier !== 'free' ? ' paid' : '')}>{titleCase(row.tier)}</span>
        ) : (
          <span className="dash">—</span>
        )}
        {row.org_count > 1 ? <div className="sm">{row.org_count} orgs</div> : null}
      </>
    ),
  },
  {
    ...CREDITS_CSV,
    width: '100px',
    numeric: true,
    sortValue: row => row.credits_30,
    // Three distinct states: no playground org at all (—), an org that burned nothing (0), and
    // a real figure. Collapsing the first two is what the em-dash exists to prevent.
    cell: row => {
      if (row.credits_30 == null) return <span className="dash">—</span>;
      if (!row.credits_30) return <span className="zero">0</span>;
      return (
        <>
          {formatCredits(row.credits_30)} <TrendChip percent={row.credits_trend_pct} />
        </>
      );
    },
  },
  {
    ...CONTACTS_CSV,
    width: '76px',
    numeric: true,
    hint: 'Contacts we can actually write to: has an email or a LinkedIn URL, minus anyone '
      + 'opted out, bounced or on Amplemarket’s exclusion list. A zero means this account '
      + 'cannot be worked at all.',
    sortValue: row => reachableContacts(row).length,
    cell: row => {
      const reachable = reachableContacts(row).length;
      if (reachable) return formatCount(reachable);
      // Styled as a warning rather than a muted zero. Every other zero on this table is a
      // thing not yet done; this one is a thing that cannot be done, and it is the only cell
      // that tells somebody to stop and go find a contact.
      return <span className="noway" title="No contact on this account can be written to">0</span>;
    },
  },
  {
    ...LAST_OUT_CSV,
    width: '178px',
    hint: 'The most recent outbound touch by anybody, who it went to, and who sent it.',
    // Sorts on the date of the last outbound touch by anyone, which is what the cell shows --
    // not on `last_touch`, which is the owner's own and would order the column by a different
    // number from the one being read.
    sortValue: row => lastOutbound(row)?.day ?? null,
    cell: (row, context) => <LastOutCell row={row} context={context} />,
  },
  {
    ...AE_CSV,
    // A long name wraps at its space rather than clipping.
    width: '96px',
    sortValue: row => row.account_owner,
    cell: row => <span className="sm aename">{row.account_owner || '—'}</span>,
  },
];

// Rollout-off keeps the original Signal waterfall and hides Rank.
const SCOUT_COLUMN_KEYS = new Set(['rank']);

export function columnsForViewer(viewer: Viewer | undefined): BookColumn[] {
  if (viewer?.sees_scout_columns) return EXECUTION_COLUMNS;
  return EXECUTION_COLUMNS.filter(column => !SCOUT_COLUMN_KEYS.has(column.key)).map(column =>
    column.key === 'why_now' ? { ...column, ...SIGNAL_CSV,
      hint: 'The strongest reason in the book snapshot.',
      sortValue: (row: RankedAccount) => { const reason = whyNow(row, row.book_today); return reason ? WHY_NOW_RANK[reason.kind] : null; },
      cell: (row: RankedAccount) => <WhyNowCell row={row} />,
    } : column);
}

// The classes a <td> needs that its cell renderer cannot set on itself.
export function cellClassName(column: BookColumn, row: RankedAccount): string {
  const classes: string[] = [];
  if (column.numeric) classes.push('num');
  if (column.key === 'account') classes.push('acctname');
  if (column.key === 'account_owner') classes.push('sm');

  // No `stale` class here any more. The cell used to tint its whole date orange past the stale
  // threshold, which collided with the orange that means "a rep who is not the owner was last
  // in here". One colour, one meaning: orange in this column is the collision, and the age is
  // stated as a number instead.


  // A zero count is styled muted rather than as a value, the way it always was. `reachable`
  // is excluded: its zero is a warning rather than an absence, and it styles itself.
  // `rank` is excluded because the field on the row is `ranking`, and a missing rank
  // is an em-dash, not a muted zero.
  if (column.numeric && column.key !== 'credits_30' && column.key !== 'reachable'
      && column.key !== 'rank'
      && !(row as unknown as Record<string, number>)[column.key]) {
    classes.push('zero');
  }
  return classes.join(' ');
}
